/**
 * Entrada pelo Hub Galeria.
 *
 * O Hub abre o app com `?token=`; o token fica no `localStorage` e vai em
 * `Authorization: Bearer` a cada chamada. Quem decide se ele vale é o backend, que
 * pergunta ao Hub — aqui só se guarda e se repassa. A URL do Hub vem de
 * `/auth/config`, não do bundle, para não ficar congelada num build.
 *
 * Regra da casa: nenhuma tela entre a pessoa e o login. Sem sessão, redireciona na
 * hora; enquanto autentica, tela preta. Só o token recusado tem tela — com o motivo e
 * um botão, nunca com redirect automático, senão vira loop.
 */

export type Role = 'viewer' | 'analyst' | 'admin'

export interface AuthConfig {
  mode: 'hub' | 'off'
  hub_url: string
  project_id: string
  project_name: string
  roles: Role[]
}

export interface Session {
  user_id: string
  email: string
  name: string | null
  photo_url: string | null
  role: Role
  type: 'internal' | 'external'
}

export type AuthState =
  | { status: 'loading' }
  | { status: 'ready'; session: Session; config: AuthConfig }
  | { status: 'refused'; message: string; config: AuthConfig }
  | { status: 'failed'; message: string }

const TOKEN_KEY = 'galeria_hub_token'
const ROLE_ORDER: Role[] = ['viewer', 'analyst', 'admin']

let config: AuthConfig | null = null

export function can(session: Session, role: Role): boolean {
  return ROLE_ORDER.indexOf(session.role) >= ROLE_ORDER.indexOf(role)
}

export function authHeaders(): Record<string, string> {
  const token = readToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/** Separa o `?token=` da URL. A URL limpa preserva o caminho e o resto da query —
 * o Hub devolve a pessoa para onde ela estava, e isso não pode se perder. */
export function takeUrlToken(href: string): { token: string | null; cleanUrl: string } {
  const url = new URL(href)
  const token = url.searchParams.get('token')
  url.searchParams.delete('token')
  return { token, cleanUrl: `${url.pathname}${url.search}${url.hash}` }
}

/** `?project` faz o Hub abrir como "Entrar em <Projeto>"; `?redirect` é para onde ele
 * devolve a pessoa — e só devolve se a origem estiver registrada no projeto. */
export function hubLoginUrl(cfg: AuthConfig, back: string): string {
  return `${cfg.hub_url}?project=${encodeURIComponent(cfg.project_id)}&redirect=${encodeURIComponent(back)}`
}

/** `?redirect` é obrigatório também no logout: o `/logout` do Hub repassa a query para
 * a tela de login, e sem ele quem entra de novo fica parado no portal. */
export function hubLogoutUrl(cfg: AuthConfig, origin: string): string {
  return `${cfg.hub_url}/logout?project=${encodeURIComponent(cfg.project_id)}&redirect=${encodeURIComponent(`${origin}/`)}`
}

export function redirectToHub(): void {
  if (!config || config.mode !== 'hub') return
  window.location.replace(hubLoginUrl(config, window.location.href))
}

export function logout(): void {
  clearToken()
  if (config?.mode === 'hub') window.location.href = hubLogoutUrl(config, window.location.origin)
}

/** Chamado pelo cliente de API quando o backend responde 401 no meio do uso: o token
 * expirou ou o acesso foi revogado no Hub. Volta para o Hub em silêncio, como no boot. */
export function sessionExpired(): void {
  clearToken()
  redirectToHub()
}

export async function boot(): Promise<AuthState> {
  const response = await fetch('/auth/config', { headers: { Accept: 'application/json' } })
  if (!response.ok) return { status: 'failed', message: `${response.status} em /auth/config` }
  config = (await response.json()) as AuthConfig

  if (config.mode === 'hub') {
    const { token: urlToken, cleanUrl } = takeUrlToken(window.location.href)
    if (urlToken) {
      storeToken(urlToken)
      window.history.replaceState(window.history.state, '', cleanUrl)
    }
    if (!readToken()) {
      redirectToHub()
      return { status: 'loading' }
    }
    const me = await fetch('/auth/me', { headers: { Accept: 'application/json', ...authHeaders() } })
    if (me.status === 401) {
      clearToken()
      // Token recém-chegado do Hub e recusado é decisão do Hub (sem acesso, revogado):
      // redirecionar de volta faria loop. Token velho do `localStorage` só expirou.
      if (urlToken) {
        return {
          status: 'refused',
          message: 'O Hub Galeria não liberou seu acesso a este projeto.',
          config,
        }
      }
      redirectToHub()
      return { status: 'loading' }
    }
    if (!me.ok) return { status: 'failed', message: await me.text() }
    return { status: 'ready', session: (await me.json()) as Session, config }
  }

  const me = await fetch('/auth/me', { headers: { Accept: 'application/json' } })
  if (!me.ok) return { status: 'failed', message: `${me.status} em /auth/me` }
  return { status: 'ready', session: (await me.json()) as Session, config }
}

function readToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

function storeToken(token: string): void {
  try {
    window.localStorage.setItem(TOKEN_KEY, token)
  } catch {
    // Sem storage (aba privada restrita): a sessão vale só até recarregar.
  }
}

function clearToken(): void {
  try {
    window.localStorage.removeItem(TOKEN_KEY)
  } catch {
    // idem
  }
}
